import { button } from '@synoptic/ui-kit/button/button.css.js';
import { inputLabel } from '@synoptic/ui-kit/input/input.css.js';
import { black, gray, white } from '@synoptic/ui-kit/palette.js';
import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.js';
import { darkTheme } from '@synoptic/ui-kit/theme/theme.css.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.js';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, style } from '@vanilla-extract/css';

export const pageTitle = style([
  {
    display: 'flex',
    gap: space.s4,
    alignItems: 'center',
  },
  responsiveStyle({
    mobile: { ...typography.labelMd },
    laptop: {
      ...typography.labelLg,
    },
  }),
]);

export const searchInput = style([
  responsiveStyle({
    mobile: {
      width: '100%',
    },
    tablet: {
      width: '210px',
    },
  }),
]);

export const panelSeparator = style({
  marginBlock: space.s12,
});

const floatingPanel = style({
  position: 'absolute',
  zIndex: 1,

  selectors: {
    '&:is(&)': {
      borderRadius: borderRadius.r8,
      backgroundColor: `light-dark(${white[700]}, ${black[700]})`,
      backdropFilter: 'blur(12px) saturate(2.8) brightness(1.25) contrast(1)',
      backgroundClip: 'padding-box',
      backgroundOrigin: 'padding-box',
      border: `1px solid light-dark(${black[50]}, ${white[200]})`,
      outline: 0,
    },
  },
});

export const floatingOptionsPanel = style([
  floatingPanel,
  {
    top: space.s12,
    right: space.s12,
    padding: space.s12,
    ':empty': {
      display: 'none',
    },
  },
  responsiveStyle({
    mobile: { width: 250 },
    tablet: { width: 300 },
    laptop: { width: 320 },
  }),
]);

export const panelTitle = style([
  {
    marginBottom: space.s8,
  },
  responsiveStyle({
    mobile: {
      ...typography.labelSm,
    },
    laptop: {
      ...typography.labelMd,
    },
  }),
]);

export const inlineOptionGroup = style({
  display: 'flex',
  gap: space.s8,
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: space.s8,
});

export const optionLabel = style([
  inputLabel,
  {
    color: themeVars.container.foreground.secondary,
    selectors: {
      [`${inlineOptionGroup} &`]: {
        marginBottom: 0,
      },
    },
  },
]);

export const optionText = style([
  {
    color: themeVars.container.foreground.primary,
    selectors: {
      [`${inlineOptionGroup} &`]: {
        textAlign: 'right',
        textWrap: 'balance',
      },
    },
  },
  responsiveStyle({
    mobile: {
      ...typography.bodySm,
    },
    laptop: {
      ...typography.bodyMd,
    },
  }),
]);
globalStyle(`${optionText} a`, {
  color: themeVars.container.foreground.primary,
});

export const optionStack = style({
  display: 'grid',
  width: '100%',
});

export const floatingTitlePanel = style([
  floatingPanel,
  {
    position: 'absolute',
    top: space.s12,
    display: 'flex',
    flexWrap: 'wrap',
    gap: space.s16,
    rowGap: space.s12,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: space.s12,
  },
  responsiveStyle({
    mobile: {
      left: space.s12,
      right: space.s12,
    },
    tablet: {
      right: 'unset',
      marginRight: 340,
    },
    laptop: {
      left: space.s24,
    },
  }),
]);

export const floatingAdPanel = style([
  floatingPanel,
  {
    position: 'absolute',
    bottom: space.s12,
    left: space.s24,
    maxWidth: 'min(350px, calc(100% - 64px))',
    transition: '0.15s background-color',

    ':hover': {
      backgroundColor: themeVars.container.primary.background.hover,
    },
  },
  responsiveStyle({
    mobile: {
      left: space.s12,
    },
    laptop: {
      left: space.s24,
    },
  }),
]);

export const floatingWatermarkPanel = style([
  floatingPanel,
  typography.labelMd,

  {
    position: 'absolute',
    bottom: space.s12,
    right: space.s12,
    padding: `${space.s12}px ${space.s16}px`,
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    gap: space.s4,
    color: themeVars.container.foreground.primary,
  },
]);

export const adBody = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s4,
  color: themeVars.container.foreground.secondary,
});

export const adTitle = style([
  typography.labelMd,
  {
    color: themeVars.container.foreground.primary,
  },
]);

export const floatingNavigationPanel = style([
  floatingPanel,
  {
    bottom: space.s12,
    right: space.s12,
    borderRadius: borderRadius.r8,
  },
]);

export const floatingNavigationPanelButton = style({
  borderRadius: 0,

  selectors: {
    '&:first-child': {
      borderTopLeftRadius: space.s8,
      borderTopRightRadius: space.s8,
    },
    '&:last-child': {
      borderBottomLeftRadius: space.s8,
      borderBottomRightRadius: space.s8,
    },
  },
});

const gridLight = `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='11.75' y='9' width='0.5' height='6' fill='${black[50]}'/%3E%3Crect x='9' y='11.75' width='6' height='0.5' fill='${black[50]}'/%3E%3C/svg%3E")`;
const gridDark = `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='11.75' y='9' width='0.5' height='6' fill='${white[100]}'/%3E%3Crect x='9' y='11.75' width='6' height='0.5' fill='${white[100]}'/%3E%3C/svg%3E")`;

export const graphContainer = style({
  width: '100%',
  height: '100%',
  position: 'relative',
  backgroundColor: `light-dark(${white[900]}, ${gray[900]})`,
  backgroundImage: gridLight,
});

globalStyle(`${darkTheme} ${graphContainer}`, {
  backgroundImage: gridDark,
});

export const shareActions = style({
  display: 'flex',
  gap: space.s8,
});

globalStyle(`${shareActions} > ${button}`, {
  boxShadow: `inset 0 0 0 1px ${themeVars.controls.border.default}`,
});
